

import React, { useState, useRef, useEffect } from 'react';
import { 
  FaStar, 
  FaPlay, 
  FaPause, 
  FaPaperPlane,
  FaCalendarAlt
} from 'react-icons/fa';
import { MessageSquare } from 'lucide-react';
import ContactForm from './ContactForm'; // Import the form component
import "../../Component/Home/theme.css"; // Import the CSS file

const ContactPage = () => {
  const videoRef = useRef(null);
  const formRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  
  // Add font styles to document head
  useEffect(() => {
    // Add Bell MT and Inter fonts
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.cdnfonts.com/css/bell-mt');
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
      
      .bell-mt-primary {
        font-family: 'Bell MT', serif !important;
      }
      
      .inter-secondary {
        font-family: 'Inter', sans-serif !important;
      }
      
      body {
        font-family: 'Inter', sans-serif;
      }
      
      h1, h2, h3, h4, h5, h6 {
        font-family: 'Bell MT', serif;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Check for hash on page load and scroll to form
  useEffect(() => {
    if (window.location.hash === '#contact-form') {
      setTimeout(() => {
        scrollToForm();
      }, 100);
    }

    const handleHashChange = () => {
      if (window.location.hash === '#contact-form') {
        scrollToForm();
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(e => {
        console.log("Autoplay prevented:", e);
      });
    }
  }, []);

  // Get current day for highlighting (in EST)
  const getCurrentDay = () => {
    const estNow = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date(estNow).getDay()];
  };

  // Business Hours
  const businessHours = [
    { day: 'Monday', hours: 'Closed', isClosed: true, shortDay: 'Mon' },
    { day: 'Tuesday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Tue' },
    { day: 'Wednesday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Wed' },
    { day: 'Thursday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Thu' },
    { day: 'Friday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Fri' },
    { day: 'Saturday', hours: '10:00 AM – 2:00 PM', isClosed: false, shortDay: 'Sat' },
    { day: 'Sunday', hours: 'Closed', isClosed: true, shortDay: 'Sun' }
  ];



  return (
    <div className="min-h-screen inter-secondary">
      {/* Contact Form Section */}
      <section id="contact-form" ref={formRef} className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                background: "var(--bg-primary-10)",
                border: "1px solid var(--border-primary-20)",
              }}
            >
              <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
              <span className="text-sm font-medium" style={{ color: "var(--color-primary-400)" }}>
                Get Started
              </span>
            </div>
            
            <h2 className="bell-mt-primary text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4" style={{ color: "var(--color-primary-500)" }}>
              Start Your Dream Project
            </h2>
            
            <p className="inter-secondary text-base sm:text-lg max-w-2xl mx-auto px-4" style={{ color: "var(--color-gray-600)" }}>
              Fill out the form below and our team will contact you within 24 hours to discuss your project.
            </p>

            {/* Decorative Line */}
            <div className="flex justify-center mt-6" aria-hidden="true">
              <div 
                className="w-24 h-1 rounded-full"
                style={{
                  background: "var(--gradient-primary)",
                }}
              ></div>
            </div>
          </div>

          {/* Mobile: Form first, Info second. Desktop: Grid layout */}
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Contact Form - First on Mobile, Second on Desktop */}
            <div className="order-1 lg:order-2 lg:col-span-2">
              <div 
                className="rounded-2xl sm:rounded-3xl border p-4 sm:p-6 md:p-8 shadow-lg"
                style={{
                  backgroundColor: "var(--color-white)",
                  borderColor: "var(--border-light)",
                  boxShadow: "var(--shadow-medium)",
                }}
              >
                <ContactForm />
              </div>
            </div>

            {/* Contact Info Sidebar - Second on Mobile, First on Desktop */}
            <div className="order-2 lg:order-1 lg:col-span-1">
              <div 
                className="rounded-2xl sm:rounded-3xl p-6 sm:p-8 h-full relative overflow-hidden"
                style={{
                  background: "var(--color-primary-500)",
                }}
              >
                {/* Background Decorative Elements */}
                <div 
                  className="absolute top-0 right-0 w-32 h-32 rounded-full translate-x-1/2 -translate-y-1/2 opacity-20 blur-3xl" 
                  aria-hidden="true"
                  style={{
                    backgroundColor: "var(--color-white)",
                  }}
                ></div>
                <div 
                  className="absolute bottom-0 left-0 w-32 h-32 rounded-full -translate-x-1/2 translate-y-1/2 opacity-20 blur-3xl" 
                  aria-hidden="true"
                  style={{
                    backgroundColor: "var(--color-white)",
                  }}
                ></div>

                <div className="relative z-10">
                  <div className="mb-8 sm:mb-10">
                    <h3 className="bell-mt-primary text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: "var(--color-white)" }}>
                      Business Hours
                    </h3>
                    <div className="space-y-2">
                      {businessHours.map((day, index) => {
                        const isToday = day.day === getCurrentDay();
                        return (
                          <div 
                            key={index} 
                            className="flex items-center justify-between p-3 rounded-lg"
                            style={{
                              backgroundColor: "var(--color-white)",
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              {day.isClosed ? (
                                <span className="text-lg sm:text-xl" style={{ color: "var(--color-primary-500)" }}>❌</span>
                              ) : (
                                <span className="text-lg sm:text-xl" style={{ color: "var(--color-primary-500)" }}>🕒</span>
                              )}
                              <span className="inter-secondary font-medium" style={{ color: "var(--color-primary-500)" }}>
                                {day.shortDay}
                              </span>
                            </div>
                            <span className="inter-secondary font-semibold text-sm" style={{ color: "var(--color-primary-500)" }}>
                              {day.hours}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mb-8 sm:mb-10">
                    <h3 className="bell-mt-primary text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: "var(--color-white)" }}>
                      Why Choose Us
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      {[
                        { icon: "👥", title: 'Expert Team', desc: '15+ years of specialized experience' },                    
                        { icon: "🛡️", title: 'Warranty', desc: '5-year limited warranty' }
                      ].map((item, index) => (
                        <div key={index} className="flex items-start space-x-3 sm:space-x-4">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-white)" }}>
                            <span style={{ color: "var(--color-primary-500)", fontSize: "1.5rem" }}>{item.icon}</span>
                          </div>
                          <div>
                            <h4 className="bell-mt-primary font-semibold text-sm sm:text-base" style={{ color: "var(--color-white)" }}>{item.title}</h4>
                            <p className="inter-secondary text-xs sm:text-sm mt-1" style={{ color: "var(--color-white)" }}>{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="bell-mt-primary text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: "var(--color-white)" }}>
                      Contact Directly
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      <a 
                        href="tel:8624506069"
                        className="inter-secondary flex items-center justify-between p-3 rounded-xl transition-all text-sm sm:text-base"
                        style={{
                          backgroundColor: "var(--color-white)",
                          color: "var(--color-primary-500)",
                          border: "1px solid var(--border-white-10)",
                        }}
                      >
                        <span className="font-medium">Call Now</span>
                        <span className="font-bold text-sm">(862) 450-6069</span>
                      </a>
                      <a 
                        href="mailto:Contactus@laminance.com"
                        className="inter-secondary flex items-center justify-between p-3 rounded-xl transition-all text-sm sm:text-base"
                        style={{
                          backgroundColor: "var(--color-white)",
                          color: "var(--color-primary-500)",
                          border: "1px solid var(--border-white-10)",
                        }}
                      >
                        <span className="font-medium">Email Us</span>
                        <span className="font-bold text-sm">Contactus@laminance.com</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Back to Form Button (Mobile Only) */}
      <div className="lg:hidden fixed bottom-6 right-6 z-50">
        <button
          onClick={scrollToForm}
          className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          style={{
            background: "var(--gradient-primary)",
          }}
        >
          <FaPaperPlane className="text-white text-xl" />
        </button>
      </div>
    </div>
  );
};

export default ContactPage;